import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
  QueryList,
  ViewChild,
  ViewChildren,
} from "@angular/core";
import { Action, select, Store } from "@ngrx/store";
import { Observable, Subject } from "rxjs";
import { map, takeUntil, tap } from "rxjs/operators";
import {
  IntroductionEditorComponent,
  MediaEditorComponent,
} from "src/app/editors/components";
import { SolveSubActivity, SolveSubActivityStep } from "src/app/models";
import { AppStoreState } from "src/app/store";
import { ValidationListComponent } from "src/app/validation-builder";
import { ActivityEditorBaseComponent } from "../activity";
import { ActivityTogglesComponent } from "../activity-toggles";
import { EditInMakecodeComponent } from "../edit-in-makecode";
import { SolveActivityStepComponent } from "./solve-activity-step.component";
import {
  addStep,
  deleteStep,
  deleteSubActivity,
  updateSubActivity,
} from "./store/actions";
import { selectSteps, levelId } from "./store/selectors";

@Component({
  selector: "app-solve-sub-activity",
  templateUrl: "./solve-sub-activity.component.html",
  styleUrls: ["./solve-sub-activity.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SolveSubActivityComponent extends ActivityEditorBaseComponent {
  constructor(store$: Store<AppStoreState>) {
    super(store$);
    this.activityType = "Solve";
  }
  @HostBinding("class") class = "app-solve-sub-activity";
  @Input() shouldHaveMedia = false;
  @Input() activity: SolveSubActivity;
  @Input() index: number;

  steps$: Observable<SolveSubActivityStep[]> = this.store$.pipe(
    select(selectSteps),
    map((steps) => steps.filter((s) => s.subActivityId === this.activity.id)),
    tap((steps) => (this.mySteps = steps))
  );

  @ViewChild(IntroductionEditorComponent)
  introduction: IntroductionEditorComponent;

  @ViewChild(ActivityTogglesComponent)
  activityToggles: ActivityTogglesComponent;

  @ViewChild(EditInMakecodeComponent)
  codeEditor: EditInMakecodeComponent;

  @ViewChild(ValidationListComponent)
  validationItems: ValidationListComponent;

  @ViewChildren(SolveActivityStepComponent)
  steps = new QueryList<SolveActivityStepComponent>();

  @ViewChild(MediaEditorComponent)
  mediaEditor: MediaEditorComponent;
  levelId = 0;
  levelId$ = this.store$
    .select(levelId)
    .pipe(takeUntil(this.destroyed$))
    .subscribe((l) => {
      this.levelId = l;
    });
  get valid(): boolean {
    return !!(
      this.title?.valid &&
      this.introduction?.valid &&
      this.activityToggles?.valid &&
      this.codeEditor?.valid &&
      this.validationItems?.valid &&
      (this.mediaEditor?.valid || !this.mediaEditor) &&
      this.steps?.map((s) => s).every((s) => s.valid)
    );
  }

  get dirty(): boolean {
    return !!(
      this.title?.dirty ||
      this.introduction?.dirty ||
      this.activityToggles?.dirty ||
      this.codeEditor?.dirty ||
      this.validationItems?.dirty ||
      this.mediaEditor?.dirty ||
      this.steps?.map((s) => s).find((s) => s.dirty)
    );
  }

  private mySteps: SolveSubActivityStep[] = [];

  addStep(): void {
    this.store$.dispatch(addStep({ subActivityId: this.activity.id }));
  }

  deleteStep(index: number): void {
    this.store$.dispatch(deleteStep({ step: this.mySteps[index] }));
  }

  protected getUpdateAction(): Action {
    return updateSubActivity({
      subActivity: {
        ...this.activity,
        title: this.title.value,
        introduction: this.introduction.value,
        hasLowThreshold: this.activityToggles.hasLowThreshold,
        lowThreshold: this.activityToggles.lowThreshold,
        hasPassingThreshold: this.activityToggles.hasPassingThreshold,
        passingThreshold: this.activityToggles.passingThreshold,
        peerReview: this.activityToggles.peerReview,
        numOfReviews: this.activityToggles.numOfReviews,
        project: this.codeEditor.project,
        validationItems: this.validationItems.items,
        steps: this.steps.filter((s) => s.dirty).map((s) => s.value),
      },
      mediaFile: this.mediaEditor?.mediaValue || null,
      levelId: this.levelId,
    });
  }

  protected getDeleteAction(): Action {
    return deleteSubActivity({ subActivity: this.activity });
  }
}
